import { DEFAULT_USER_SETTINGS, ORGANIZATIONS, USER_ROLES_WITHOUT_GUEST_AND_USER } from 'cpool-constants';
import { User_AdviserAbsenceScheduled, User_BucketAdded } from 'cpool-events';
import {
    divisionId,
    divisionKind,
    Phone,
    schemaEuropacePlatforms,
    schemaGroupAssignment,
    schemaId,
    schemaUserCommon,
    schemaUserSettings,
} from 'cpool-schemas';
import { z } from 'zod';

// TODO we should use the discriminatedUnion User-Schema in the future...  ---->  packages/cpool-schemas/src/User/User.ts

export const UsersTableDefinitionZod = z.object({
    absence: z.array(User_AdviserAbsenceScheduled.prototype.getSchema().merge(z.object({ eventId: z.string() }))),
    assignable: z.boolean(),
    avatar: z.string().nullable(),
    breakingEvent: z.string(),
    buckets: z.array(User_BucketAdded.prototype.getSchema().shape.type),
    certData: z.string().nullable(),
    certFileName: z.string().nullable(),
    certKey: z.string().nullable(),
    createdBy: schemaUserCommon.shape.createdBy,
    deactivated: z.boolean(),
    delegateAppointedAt: z.coerce.date().nullable(),
    // only set when a user (e.g. team leader) appointed the delegate manually,
    // null for saga-scheduled appointments (adviser absence flow)
    delegateAppointedById: schemaId.nullable(),
    delegateId: schemaId.nullable(),
    email: schemaUserCommon.shape.email,
    europacePlatforms: schemaEuropacePlatforms.nullable(),
    firstName: schemaUserCommon.shape.firstName,
    gender: schemaUserCommon.shape.gender,
    id: schemaId,
    lastName: schemaUserCommon.shape.lastName,
    // rows written before lock tracking carry no value — they read back as unlocked
    locked: z.boolean().default(false),
    organization: z.nativeEnum(ORGANIZATIONS).nullish(),
    ownedBuckets: z.array(User_BucketAdded.prototype.getSchema().shape.type),
    password: z.string().nullable(),
    // NOTE: is not included in the regular user schema
    phone: Phone.nullable(),
    pid: schemaUserCommon.shape.pid.unwrap().default(null),
    procheckId: z.string().min(1).nullish().default(null),
    registered: z.boolean(),
    role: z.nativeEnum(USER_ROLES_WITHOUT_GUEST_AND_USER),
    secret: z.string().nullable(),
    // rows written before the settings feature carry no value — they read back as the default
    settings: schemaUserSettings.default(DEFAULT_USER_SETTINGS),
    signatureId: schemaId.nullable(),
    smtp: schemaUserCommon.shape.smtp,
    state: z.number(),
    teamId: schemaId.nullish().default(null),
    title: schemaUserCommon.shape.title,
    tmpSecret: z.string().nullable(),
});

export type UsersTableSchema = z.infer<typeof UsersTableDefinitionZod>;

// not stored on the user, resolved upwards via team -> division -> corporation
export const UserGroupContextZod = z.object({
    corporationId: schemaId.nullable(),
    divisionId: divisionId.nullable(),
    divisionKind: divisionKind.nullable(),
    groupAssignment: schemaGroupAssignment.nullable(),
});

export type UserGroupContext = z.infer<typeof UserGroupContextZod>;

export const NonSensitiveUsersTableDefinitionZod = UsersTableDefinitionZod.omit({
    certData: true,
    certFileName: true,
    certKey: true,
    europacePlatforms: true,
    password: true,
    secret: true,
    smtp: true,
    tmpSecret: true,
});

export type NonSensitiveUsersTableSchema = z.infer<typeof NonSensitiveUsersTableDefinitionZod>;
